import React from "react"
import ReactApexChart from "react-apexcharts"

/**
 * A 100% stacked bar chart component using ApexCharts
 *
 * @param {Object} props Component props
 * @param {Array} props.data The data array for the chart
 * @param {string} props.series1Name Name of first data series
 * @param {string} props.series2Name Name of second data series
 * @param {string} props.series3Name Optional name of third data series
 * @param {string} props.xAxisKey Data key for x-axis categories
 * @param {string} props.xAxisLabel Label for x-axis
 * @param {string} props.yAxisLabel Label for y-axis
 * @param {string} props.title Chart title
 * @param {Array} props.colors Array of colors for the bars
 * @param {number} props.height Chart height
 * @param {boolean} props.brokenYAxis Whether to break the y-axis at 0
 * @returns {JSX.Element}
 */
const FullStackedBarGraph = ({
	data,
	series1Name,
	series2Name,
	series3Name = null,
	xAxisKey,
	xAxisLabel,
	yAxisLabel,
	title = "",
	colors = ["#352D66", "#6955EA", "#f87171"],
	height = 350,
	brokenYAxis = false
}) => {
	const options = {
		chart: {
			type: "bar",
			height: height,
			stacked: true,
			stackType: "100%",
			toolbar: {
				show: false
			},
			fontFamily: "Manrope, sans-serif"
		},
		plotOptions: {
			bar: {
				horizontal: false,
				columnWidth: "50%",
				borderRadius: 3,
				borderRadiusApplication: "end",
				borderRadiusWhenStacked: "last"
			}
		},
		legend: {
			show: false
		},
		dataLabels: {
			enabled: false
		},
		stroke: {
			width: 0,
			colors: ["#fff"]
		},
		xaxis: {
			categories: data.map(item => item[xAxisKey]),
			labels: {
				style: {
					fontSize: "12px",
					colors: ["#8c8c8c"]
				}
			},
			title: {
				text: xAxisLabel,
				style: {
					fontSize: "12px",
					color: "#8c8c8c"
				}
			}
		},
		yaxis: {
			title: {
				text: yAxisLabel,
				style: {
					fontSize: "12px",
					color: "#8c8c8c"
				}
			},
			labels: {
				formatter: function (value) {
					return `${Math.round(value)}%`
				},
				style: {
					fontSize: "12px",
					colors: ["#8c8c8c"]
				}
			}
		},
		colors: colors,
		grid: {
			borderColor: "#f1f1f1"
		},
		tooltip: {
			y: {
				formatter: function (value, { series, seriesIndex, dataPointIndex }) {
					// Calculate the total for this data point across all series
					let total = 0
					series.forEach(s => {
						total += s[dataPointIndex]
					})

					// Calculate percentage
					const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0

					return `${value} (${percentage}%)`
				}
			}
		},
		fill: {
			opacity: 1,
			type: "solid"
		}
	}

	const series = [
		{
			name: series1Name.charAt(0).toUpperCase() + series1Name.slice(1),
			data: data.map(item => item[series1Name] || 0)
		},
		{
			name: series2Name.charAt(0).toUpperCase() + series2Name.slice(1),
			data: data.map(item => item[series2Name] || 0)
		}
	]

	// Add third series if provided
	if (series3Name) {
		series.push({
			name: series3Name.charAt(0).toUpperCase() + series3Name.slice(1),
			data: data.map(item => item[series3Name] || 0)
		})
	}

	return (
		<>
			{title && (
				<h5 className="text-xl font-bold leading-none text-gray-900 mb-3">{title}</h5>
			)}
			<div className="">
				<ReactApexChart options={options} series={series} type="bar" height={height} />
			</div>
		</>
	)
}

export default FullStackedBarGraph
